import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CardActions,
    CardContent,
    CircularProgress,
    Typography,
} from "@mui/material";
import {
    ADVISORY_ROUTE_TEXT,
    FORECAST_UPDATE_BUTTON,
    LEVEL_FIVE,
    LEVEL_ONE,
    MORE_OPTIONS,
    REVISE_TRIP,
} from "../../UserVisibleStrings";
import { FieldErrors, FieldPath, FieldValues, get, RegisterOptions, useForm, UseFormRegister, } from "react-hook-form";
import { RouteRequestInput, Trip } from "../../../generated/gpb/api/route";
import { useAddTripMutation, } from "../../../services/routing";
import { DateTime } from "luxon";
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../../../static/css/autocomplete.css";
import { TripWaypointsForm } from "./TripWaypointsForm";
import { TripOptionsForm } from "./TripOptionsForm";
import {
    clientResolver,
    convertWaypoints,
    dateSchemaToDate,
    dateToSchema,
    DEFAULT_MAX_SPEED,
    defaultWaypoint,
    formToTripRequest,
    RouteRequestFormData,
    RouteRequestInputTypeStrings,
    tripToForm,
} from "./TripFormTypeConversions";
import { useIsMobile } from "../../../features/pageWidth/pageWidthSlice";
import { ZIndexLevels } from "../../risk/geoUtils";
import { getTripDrawerWidth } from "../../tripSidebar/TripDrawer";
import useIdentity from "../../../app/auth";
import { useRiskViewerState, WaypointExpanded } from "../../../features/riskViewer/riskViewerSlice";
import ConfirmEditTypeDialog, { ConfirmType } from "./ConfirmEditTypeDialog";
import { ExpandMore } from "@mui/icons-material";
import { useUserViewOverride } from "../../../features/riskViewer/userViewOverrideSlice";
import { validateLengthOfStay } from "./validation";

export function useMuiFieldRegister<
    TFieldValues extends FieldValues = FieldValues,
    TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>( register: UseFormRegister<TFieldValues>, errors: FieldErrors<TFieldValues> )
{
    return (
        field: TFieldName,
        options?: RegisterOptions<TFieldValues, TFieldName>
    ) =>
    {
        const error = get( errors, field );
        return {
            ...register( field, options ),
            error: ( Boolean( error ) ),
            helperText: error?.message,
        };
    };
}

export function useErrorProps<
    TFieldValues extends FieldValues = FieldValues,
    TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>( errors: FieldErrors<TFieldValues> )
{
    return ( ...fields: Array<TFieldName> ) =>
    {
        for ( const field of fields )
        {
            const error = get( errors, field );
            if ( Boolean( error ) )
            {
                return {
                    error: true,
                    helperText: error?.message,
                };
            }
        }
        return {
            error: false,
        };
    };
}

export interface TripFormParams
{
    readonly defaultValues?: Trip;
    readonly routeRequestInput: RouteRequestInput | undefined;
    readonly isAdvisory: boolean;
    readonly onSubmit?: () => void;
    readonly onCancel?: () => void;
    readonly backgroundColor: string;
    readonly isForecastUpdate: boolean;
}

export function TripForm( props: TripFormParams )
{
    const { defaultValues, onCancel, routeRequestInput } = props;
    const [ showConfirm, setShowConfirm ] = useState( false );
    const [ expandAccordion, SetExpandAccordion ] = useState( false );
    const isMobile = useIsMobile();
    const isCreateMode = defaultValues === undefined;

    const userViewOverride = useUserViewOverride();
    const { isAdmin } = useIdentity( userViewOverride );

    const defaultStartTime = new Date();
    defaultStartTime.setHours( 8 );
    defaultStartTime.setMinutes( 0 );

    const defaultEndTime = new Date();
    defaultEndTime.setHours( 17 );
    defaultEndTime.setMinutes( 0 );

    const advisoryEndTime = new Date();
    advisoryEndTime.setHours( 23 );
    advisoryEndTime.setMinutes( 59 );
    advisoryEndTime.setSeconds( 59 );


    const parsedDefaultValues: RouteRequestFormData = useMemo<RouteRequestFormData>( () => defaultValues !== undefined && routeRequestInput !== undefined
        ? tripToForm( defaultValues, routeRequestInput )
        : {
            isAdvisory: props.isAdvisory,
            name: "",
            dailyStartTime: defaultStartTime,
            dailyDesiredEndTime: props.isAdvisory ? advisoryEndTime : defaultEndTime,
            dailyFinalEndTime: props.isAdvisory ? advisoryEndTime : defaultEndTime,
            startDate: dateToSchema( new Date() ),
            maxDrivingSpeed: DEFAULT_MAX_SPEED,
            overrideMaxSpeed: false,
            maxRiskLevel: props.isAdvisory ? LEVEL_FIVE : LEVEL_ONE,
            heightFeet: undefined,
            heightInches: undefined,
            avoidTolls: false,
            waypoints: [ defaultWaypoint, defaultWaypoint ],
            weightPounds: undefined,
            flexibleStartDate: false,
            requestType: props.isAdvisory ? RouteRequestInputTypeStrings[2] : RouteRequestInputTypeStrings[1],
            submitType: ConfirmType.CREATE,
            includeUS: false,
            includeMexico: false,
            includeCanada: false,
        }, [ defaultValues, routeRequestInput ] );

    const formParent = useForm<RouteRequestFormData>( {
        mode: "onChange",
        resolver: clientResolver,
        shouldUseNativeValidation: false,
        defaultValues: parsedDefaultValues,
    } );
    const { handleSubmit, formState, reset, setValue, clearErrors } = formParent;

    const includeMexico = formParent.watch( "includeMexico" );
    const heightFeet = formParent.watch( "heightFeet" );
    const heightInches = formParent.watch( "heightInches" );
    const weightPounds = formParent.watch( "weightPounds" );

    const { waypoints = [] } = useRiskViewerState();
    useEffect( () =>
    {
        const waypointExpanded: Array<WaypointExpanded> = convertWaypoints( waypoints );
        while ( waypointExpanded.length < 2 )
        {
            waypointExpanded.push( defaultWaypoint );
        }
        setValue( "waypoints", waypointExpanded );
        if ( waypointExpanded.find( waypoint =>
        {
            return waypoint.location.locationName !== "";
        } ) !== undefined )
        {
            void formParent.trigger();
        }
    }, [ waypoints ] );

    useEffect( () =>
    {
        formParent.setValue( "isAdvisory", props.isAdvisory );

        void formParent.trigger( [
            "dailyStartTime",
            "dailyDesiredEndTime",
            "dailyFinalEndTime",
        ] );
    }, [ props.isAdvisory ] );
    const { errors, isSubmitting } = formState;

    useEffect( () =>
    {
        // The admin page sets parsedDefaultValues late, and the react form uses the initial value for defaultValues
        // and can only update with a call to reset.
        reset( parsedDefaultValues );
    }, [ parsedDefaultValues ] );
    // TODO: this is code for async server validation
    // work around weird issues of isSubmitting value not updating inside the resolver
    // const isSubmittingRef = useRef(isSubmitting);
    // useEffect(() => {
    //   isSubmittingRef.current = isSubmitting;
    // }, [isSubmitting]);
    const rootError = errors.root?.message;
    // Note: field name is in Object.keys( errors )[0]
    const localError = Object.keys( errors ).length === 0 ? undefined
        : Object.values( errors )[0].message;
    const errorMessage = rootError === undefined ? localError : rootError;
    const disableSubmission = isSubmitting || Object.keys( errors ).length !== 0;
    const [ addTrip ] = useAddTripMutation();


    const onSubmit = async ( data: RouteRequestFormData ) =>
    {
    // Dates in the past are already disallowed on the DatePicker, but if you edit an older trip, it was possible
    // to submit a date in the past.  So additional validation is needed prior to the submit (note that neither
    // changing the mode to {"onChange", "onSubmit", "all"} nor adding validation to the component will trigger earlier
    // validation as the field can remain unmodified.
    // For now, ignore the time
        const currentDate = new Date();
        const waypointStartHour = ( data.waypoints.at( 0 )?.startTime?.hour ) !== undefined
            ? data.waypoints.at( 0 )?.startTime?.hour
            : data.dailyStartTime.getHours();
        const waypointStartMinute = ( data.waypoints.at( 0 )?.startTime?.minute ) !== undefined
            ? data.waypoints.at( 0 )?.startTime?.minute
            : data.dailyStartTime.getMinutes();
        const startDateWithoutTime = dateSchemaToDate( data.startDate );
        const currentDateWithoutTime = new Date( new Date( ).setHours( 0, 0, 0, 0 ) );
        const startDate = dateSchemaToDate( data.startDate, waypointStartHour, waypointStartMinute );
        if (
            !isAdmin &&
            DateTime.fromJSDate( startDateWithoutTime ).toMillis() <
            DateTime.fromJSDate( currentDateWithoutTime ).toMillis()
        )
        {
            formParent.setError( "startDate", {
                message: "Start date cannot be in the past ",
            } );
            return;
        }
        else if (
            !isAdmin &&
      DateTime.fromJSDate( startDate ).toMillis() + 3.6e+6 < // Give a one hour buffer for validation
        DateTime.fromJSDate( currentDate ).toMillis()
        )
        {
            formParent.setError( "startDate", {
                message: "Start time must be in the future - start time is currently set to " +
                    startDate.toLocaleString( "en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true
                    } ),
            } );
            return;
        }
        else
        {
            clearErrors( "startDate" );
        }

        // Overnight trips are not currently supported. For now, disallow the end time to occur before
        // the start time. Longer term, we'll consider what the algorithm can or cannot support
        if ( !props.isAdvisory && data.dailyStartTime.getTime() >= data.dailyDesiredEndTime.getTime() )
        {
            formParent.setError( "dailyDesiredEndTime", {
                message: "End time must be after start time",
            } );
            return;
        }
        if ( !props.isAdvisory && data.dailyStartTime.getTime() >= data.dailyFinalEndTime.getTime() )
        {
            formParent.setError( "dailyFinalEndTime", {
                message: "End time must be after start time",
            } );
            return;
        }
        if ( !props.isAdvisory && data.dailyDesiredEndTime.getTime() > data.dailyFinalEndTime.getTime() )
        {
            formParent.setError( "dailyFinalEndTime", {
                message: "Latest end time must be after desired end time.",
            } );
            return;
        }

        // Length-of-stay validation
        for ( const waypoint of data.waypoints )
        {
            const lengthOfStay = waypoint.pitStop?.minDurationMinutes ?? waypoint.overnightStop?.minNights ?? undefined;

            if ( lengthOfStay === undefined )
            {
                continue;
            }

            const lengthOfStayErrorMessage = validateLengthOfStay( {
                lengthOfStay,
                isPitStop: waypoint.pitStop !== undefined,
                isAdvisory: props.isAdvisory,
                dailyStartTime: data.dailyStartTime,
                dailyFinalEndTime: data.dailyFinalEndTime,
            } );

            if ( lengthOfStayErrorMessage !== undefined )
            {
                formParent.setError( "root", {
                    message: `${ lengthOfStayErrorMessage }. Please double-check your waypoints.`,
                } );

                setTimeout( () =>
                {
                    formParent.clearErrors( "root" );
                }, 5000 );

                return;
            }
        }

        // Clone data and waypoints immutably
        const newData = {
            ...data,
            waypoints: data.waypoints.map( ( wp, index ) =>
            {
                const newWaypoint = { ...wp };

                if ( index === 0 || index === data.waypoints.length - 1 )
                {
                    newWaypoint.overnightStop = { minNights: 0, maxNights: 0 };
                    newWaypoint.pitStop = undefined;
                }

                return newWaypoint;
            } ),
        };

        try
        {
            if ( defaultValues?.Id?.hexString === undefined || data.submitType === ConfirmType.CREATE )
            {
                await addTrip( formToTripRequest( newData, props.isAdvisory, undefined ) ).unwrap();
            }
            else
            {
                await addTrip( formToTripRequest( newData, props.isAdvisory, defaultValues.Id ) ).unwrap();
            }

            props?.onSubmit?.();
        }
        catch ( error )
        {
            formParent.setError( "root", {
                message:
                    "Failed to contact the server to submit your trip. Please check your internet connection try again.",
            } );
            setTimeout( () =>
            {
                formParent.clearErrors( "root" );
            }, 5000 );
        }
    };


    const handleAccordionChange = () =>
    {
        SetExpandAccordion( !expandAccordion );
    };

    const buttonText = props.isAdvisory ? ADVISORY_ROUTE_TEXT : props.isForecastUpdate ? FORECAST_UPDATE_BUTTON : REVISE_TRIP;
    const formRef = useRef<HTMLFormElement>( null );

    const onClose = ( type : ConfirmType | undefined ) =>
    {
        setShowConfirm( false ); // hide the dialog
        if ( type === undefined )
        {
            // then they clicked away and didn't select a button
            return;
        }
        setValue( "submitType", type );
        formRef.current?.requestSubmit();
    };
    const onClick = () =>
    {
        if ( props.isForecastUpdate )
        {
            // Skip the dialog, assume they want to edit in place
            onClose( ConfirmType.EDIT );
        }
        else
        {
            // Show dialog prompt to figure out if they want to edit in place or create a new entry
            setShowConfirm( true );
        }
    };
    const warningText = "Data is not saved until you click \"" + buttonText + "\"";
    return (
        <form ref = {formRef} onSubmit = {handleSubmit( onSubmit )}>

            {!!errors.root && <p>{errors.root.message}</p>}
            <Box sx = {{ position: "fixed",
                width: `calc(${getTripDrawerWidth( isMobile )} - 16px)`,
                zIndex: ZIndexLevels.TopUIElement,
                bottom: "0px",
                backgroundColor: props.backgroundColor,
                left: "0px" }}
            >
                <div>

                    <span style = {{ display: "block", textAlign: "center", fontStyle: "italic" }}>{warningText}</span>
                    <CardActions
                        sx = {{
                            display: "flex",
                            justifyContent: "flex-end",
                            margin: "10px",
                        }}
                    >
                        {isSubmitting && <CircularProgress size = {24} sx = {{ mr: 2 }}/>}
                        <Box
                            sx = {{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center"
                            }}
                        >
                            <Box sx = {{ display: "flex", alignItems: "center" }}>
                                {( errorMessage !== undefined && !isSubmitting ) && <p style = {{ color: "red" }}>{errorMessage}</p>}
                                <Button
                                    size = "medium"
                                    color = "secondary"
                                    variant = "contained"
                                    onClick = {onCancel}
                                    sx = {{ marginRight: "10px" }}
                                    disabled = {isSubmitting}
                                >
                                    Cancel
                                </Button>
                                {isCreateMode ? (
                                    <Button
                                        type = "submit"
                                        size = "medium"
                                        color = "primary"
                                        variant = "contained"
                                        disabled = {disableSubmission}
                                    >
                                        {buttonText}
                                    </Button>
                                ) : (
                                    <Button
                                        type = "button"
                                        size = "medium"
                                        color = "primary"
                                        variant = "contained"
                                        onClick = {onClick}
                                        sx = {{ marginRight: "10px" }}
                                        disabled = {disableSubmission}
                                    >
                                        {buttonText}
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </CardActions>
                </div>
            </Box>
            <ConfirmEditTypeDialog
                open = {showConfirm}
                onClose = {onClose}
            />
            <CardContent sx = {{ marginBottom: "80px", backgroundColor: props.backgroundColor }}>
                <TripWaypointsForm
                    formParent = {formParent}
                    isAdvisory = {props.isAdvisory}
                    backgroundColor = {props.backgroundColor}
                />
                <Accordion expanded = {expandAccordion} onChange = {handleAccordionChange} style = {{ backgroundColor: props.backgroundColor }}>
                    <AccordionSummary
                        expandIcon = {<ExpandMore/>}
                    >
                        <Typography>{MORE_OPTIONS}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TripOptionsForm
                            formParent = {formParent}
                            isAdvisory = {props.isAdvisory}
                            backgroundColor = {props.backgroundColor}
                        />
                    </AccordionDetails>
                </Accordion>
                <div style = {{ marginTop: "10px" }}>
                    <span style = {{ fontSize: 15 }}>* Required fields</span>
                </div>
                {includeMexico &&
                    ( heightFeet !== undefined || heightInches !== undefined || weightPounds !== undefined ) && (
                    <span style = {{ color: "red" }}>
                        Warning: SureRoute does not have height clearance data for bridges and tunnels in Mexico.
                    </span>
                )}
            </CardContent>

        </form>
    );
}
